import React, { useState } from "react";
import useVoiceInput from "./useVoiceInput";
import { MicIcon, SendIcon } from "./icons";
import { getCurrentLocale } from "../../../../../utlis/locale";
import { resolveMobilityReportUrl } from "../shareUtils";

// Local copy dictionary. SummaryReport is also rendered on the public shared
// report page (SharedActionPlanReport) with a FIXED `locale` prop, so it cannot
// use the reactive `useT` hook — it must resolve strings for the passed locale.
// Every supported language MUST have a full entry here (see CLAUDE.md → i18n).
const COPY = {
  en: {
    visit: "Visit",
    helpNeeded: "Help Needed",
    watch: "Watch",
    summary: "summary",
    recommendation: "Our Recommendation",
    editRecommendation: "Edit recommendation",
    recommendationField: "Recommendation",
    voiceInput: "Voice input",
    voice_listening: "Listening… Tap the microphone to stop.",
    voice_not_supported: "Voice input is not available on this device.",
    voice_permission_denied:
      "Microphone or speech-recognition permission was denied.",
    voice_no_speech: "No speech was detected. Please try again.",
    voice_microphone_unavailable: "The microphone is unavailable.",
    voice_failed: "Voice input could not start. Please try again.",
    saveRecommendation: "Save recommendation",
    tiers: { good: "Good", average: "Average", poor: "Poor" },
  },
  ja: {
    visit: "訪問",
    helpNeeded: "支援が必要",
    watch: "要観察",
    summary: "サマリー",
    recommendation: "推奨事項",
    editRecommendation: "推奨事項を編集",
    recommendationField: "推奨事項",
    voiceInput: "音声入力",
    voice_listening:
      "音声を認識中です。停止するにはマイクをタップしてください。",
    voice_not_supported: "この端末では音声入力を利用できません。",
    voice_permission_denied: "マイクまたは音声認識の使用が許可されていません。",
    voice_no_speech: "音声を検出できませんでした。もう一度お試しください。",
    voice_microphone_unavailable: "マイクを利用できません。",
    voice_failed: "音声入力を開始できませんでした。もう一度お試しください。",
    saveRecommendation: "推奨事項を保存",
    tiers: { good: "良好", average: "平均", poor: "不良" },
  },
  es: {
    visit: "Visita",
    helpNeeded: "Necesita ayuda",
    watch: "Vigilar",
    summary: "resumen",
    recommendation: "Nuestra recomendación",
    editRecommendation: "Editar recomendación",
    recommendationField: "Recomendación",
    voiceInput: "Entrada de voz",
    voice_listening: "Escuchando… Toca el micrófono para detener.",
    voice_not_supported:
      "La entrada de voz no está disponible en este dispositivo.",
    voice_permission_denied:
      "Se denegó el permiso del micrófono o del reconocimiento de voz.",
    voice_no_speech: "No se detectó ninguna voz. Inténtalo de nuevo.",
    voice_microphone_unavailable: "El micrófono no está disponible.",
    voice_failed: "No se pudo iniciar la entrada de voz. Inténtalo de nuevo.",
    saveRecommendation: "Guardar recomendación",
    tiers: { good: "Bueno", average: "Promedio", poor: "Deficiente" },
  },
  fr: {
    visit: "Visite",
    helpNeeded: "Aide nécessaire",
    watch: "À surveiller",
    summary: "résumé",
    recommendation: "Notre recommandation",
    editRecommendation: "Modifier la recommandation",
    recommendationField: "Recommandation",
    voiceInput: "Saisie vocale",
    voice_listening: "Écoute en cours… Touchez le micro pour arrêter.",
    voice_not_supported:
      "La saisie vocale n'est pas disponible sur cet appareil.",
    voice_permission_denied:
      "L'autorisation du micro ou de la reconnaissance vocale a été refusée.",
    voice_no_speech: "Aucune parole détectée. Veuillez réessayer.",
    voice_microphone_unavailable: "Le microphone n'est pas disponible.",
    voice_failed: "La saisie vocale n'a pas pu démarrer. Veuillez réessayer.",
    saveRecommendation: "Enregistrer la recommandation",
    tiers: { good: "Bon", average: "Moyen", poor: "Faible" },
  },
  ru: {
    visit: "Визит",
    helpNeeded: "Требуется помощь",
    watch: "Наблюдать",
    summary: "сводка",
    recommendation: "Наша рекомендация",
    editRecommendation: "Редактировать рекомендацию",
    recommendationField: "Рекомендация",
    voiceInput: "Голосовой ввод",
    voice_listening:
      "Идёт распознавание… Коснитесь микрофона, чтобы остановить.",
    voice_not_supported: "Голосовой ввод недоступен на этом устройстве.",
    voice_permission_denied:
      "В доступе к микрофону или распознаванию речи отказано.",
    voice_no_speech: "Речь не распознана. Пожалуйста, попробуйте ещё раз.",
    voice_microphone_unavailable: "Микрофон недоступен.",
    voice_failed: "Не удалось запустить голосовой ввод. Попробуйте ещё раз.",
    saveRecommendation: "Сохранить рекомендацию",
    tiers: { good: "Хорошо", average: "Средне", poor: "Плохо" },
  },
  zh: {
    visit: "就诊",
    helpNeeded: "需要帮助",
    watch: "需观察",
    summary: "摘要",
    recommendation: "我们的建议",
    editRecommendation: "编辑建议",
    recommendationField: "建议",
    voiceInput: "语音输入",
    voice_listening: "正在聆听……点按麦克风即可停止。",
    voice_not_supported: "此设备不支持语音输入。",
    voice_permission_denied: "麦克风或语音识别权限已被拒绝。",
    voice_no_speech: "未检测到语音，请重试。",
    voice_microphone_unavailable: "麦克风不可用。",
    voice_failed: "无法启动语音输入，请重试。",
    saveRecommendation: "保存建议",
    tiers: { good: "良好", average: "一般", poor: "较差" },
  },
};
const TREND_GLYPH = { up: "↑", down: "↓", flat: "→" };

const EditIcon = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

const EmphasizedText = ({ text }) => {
  const emphasis = ["8 of last 14 days", "4.8h/day"];
  const match = emphasis.find((value) => text.includes(value));
  if (!match) return text;
  const [before, after] = text.split(match);
  return (
    <>
      {before}
      <strong>{match}</strong>
      {after}
    </>
  );
};

/** Renders a section body by kind: plain-language lines, a vitals stat line, or text. */
const SectionBody = ({ section, copy }) => {
  if (section.kind === "lines") {
    const lines = section.lines || [];
    const groups = [
      {
        key: "red",
        label: copy.helpNeeded,
        items: lines.filter((l) => l.tone === "red"),
      },
      {
        key: "amber",
        label: copy.watch,
        items: lines.filter((l) => l.tone === "amber" || l.tone === "watch"),
      },
    ].filter((g) => g.items.length);
    const ungrouped = lines.filter(
      (l) => !["red", "amber", "watch"].includes(l.tone),
    );
    return (
      <div className="dap-report-grouped">
        {groups.map((g) => (
          <div className="dap-report-group" key={g.key}>
            <div className={`dap-report-group-label tone-${g.key}`}>
              {g.label}
            </div>
            {g.items.map((line, index) => (
              <div className="dap-report-group-line" key={index}>
                {line.text}
              </div>
            ))}
          </div>
        ))}
        {ungrouped.map((line, index) => (
          <div className="dap-report-group-line" key={`u${index}`}>
            {line.text}
          </div>
        ))}
      </div>
    );
  }
  if (section.kind === "stat_line") {
    return (
      <div className="dap-report-stat-line">
        {(section.items || []).map((item, index) => (
          <React.Fragment key={item.label}>
            {index ? <span className="dap-report-stat-divider">·</span> : null}
            <span>
              {item.label}{" "}
              <strong className={item.tone ? `tone-${item.tone}` : ""}>
                {item.value}
              </strong>
              {item.unit
                ? item.unit === "%"
                  ? item.unit
                  : ` ${item.unit}`
                : null}
            </span>
          </React.Fragment>
        ))}
      </div>
    );
  }
  if (section.kind === "activity_lines") {
    return (
      <div className="dap-report-activity-lines">
        {(section.lines || []).map((line, index) => (
          <div
            key={index}
            className={`dap-report-activity-line tone-${line.tone || "plain"}`}
          >
            <EmphasizedText text={line.text} />
          </div>
        ))}
      </div>
    );
  }
  if (section.kind === "insight_line") {
    const toneFor = (value) => {
      const v = (value || "").toLowerCase();
      if (v.includes("high")) return "red";
      if (v.includes("elevated") || v.includes("watch")) return "amber";
      return "normal";
    };
    return (
      <div className="dap-report-insights">
        {(section.items || []).map((item) => (
          <div className="dap-report-insight-row" key={item.label}>
            <span className="dap-report-insight-name">{item.label}</span>
            <span className="dap-report-insight-right">
              <span className={`dap-report-status-pill ${toneFor(item.value)}`}>
                {item.value}
              </span>
              {item.meta ? (
                <span className="dap-report-insight-meta">{item.meta}</span>
              ) : null}
            </span>
          </div>
        ))}
      </div>
    );
  }
  if (section.kind === "stat_line_legacy") {
    return (
      <div className="dap-stat-line">
        {(section.items || []).map((item) => (
          <span key={item.label}>
            {item.label}{" "}
            <strong className={item.tone ? `tone-${item.tone}` : ""}>
              {item.value}
            </strong>
            {item.unit
              ? item.unit === "%"
                ? item.unit
                : ` ${item.unit}`
              : null}
          </span>
        ))}
      </div>
    );
  }
  return <div className="dap-report-text">{section.text}</div>;
};

/**
 * The "Share Visit Summary" report (ported from the v7.2 family/resident report).
 * Fully data-driven from the `summary` object the backend computes — the
 * recommendation is the only locally-editable field.
 */
const SummaryReport = ({
  summary,
  onRecommendationChange,
  locale = getCurrentLocale(),
  showReportLink = false,
}) => {
  const copy = COPY[locale] || COPY.en;
  // Only a real public report link is shown (never a placeholder / 404).
  const reportUrl = showReportLink
    ? resolveMobilityReportUrl(summary.report, window.location.origin)
    : "";
  const [editing, setEditing] = useState(false);
  const [recText, setRecText] = useState(summary.recommendation?.text || "");
  const { listening, errorKey, start } = useVoiceInput((transcript) => {
    setRecText((current) => {
      const next = `${current}${current ? " " : ""}${transcript}`;
      onRecommendationChange?.(next);
      return next;
    });
  });
  const voiceStatusKey = errorKey || (listening ? "voice_listening" : null);
  const generatedDate = summary.generatedAt
    ? new Date(summary.generatedAt)
    : new Date();
  const reportDate = generatedDate.toLocaleDateString(locale || "en", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
  return (
    <div className="dap-report">
      <div className="dap-report-name notranslate" translate="no">
        {summary.patientName}
      </div>
      <div className="dap-report-date">{reportDate}</div>
      <div className="dap-report-sub">
        {summary.noun || copy.visit} {copy.summary}
        {summary.externalId ? ` · ID ${summary.externalId}` : ""}
      </div>

      {summary.recommendation ? (
        <div className="dap-report-section">
          <div className="dap-report-label">
            <span>{summary.recommendation.label || copy.recommendation}</span>
            {summary.recommendation.editable ? (
              <button
                type="button"
                className="dap-report-edit"
                aria-label={copy.editRecommendation}
                aria-pressed={editing}
                onClick={() => setEditing((value) => !value)}
              >
                <EditIcon />
              </button>
            ) : null}
          </div>
          {editing ? (
            <>
              <div className="dap-report-rec-box">
                <textarea
                  rows={3}
                  value={recText}
                  onChange={(event) => {
                    setRecText(event.target.value);
                    onRecommendationChange?.(event.target.value);
                  }}
                  aria-label={copy.recommendationField}
                />
                <button
                  type="button"
                  className={`dap-mic-button${listening ? " listening" : ""}`}
                  aria-label={copy.voiceInput}
                  aria-pressed={listening}
                  onClick={start}
                >
                  <MicIcon />
                </button>
                <button
                  type="button"
                  className="dap-send-button"
                  aria-label={copy.saveRecommendation}
                  onClick={() => setEditing(false)}
                >
                  <SendIcon />
                </button>
              </div>
              {voiceStatusKey ? (
                <div
                  className={`dap-voice-status${errorKey ? " error" : ""}`}
                  role={errorKey ? "alert" : "status"}
                >
                  {copy[voiceStatusKey]}
                </div>
              ) : null}
            </>
          ) : (
            <div className="dap-report-rec">{recText}</div>
          )}
        </div>
      ) : null}

      {(summary.sections || []).map((section) => (
        <div className="dap-report-section" key={section.label}>
          <div className="dap-report-label">
            <span>
              {section.label}
              {section.meta ? (
                <span className="meta"> {section.meta}</span>
              ) : null}
            </span>
          </div>
          <SectionBody section={section} copy={copy} />
        </div>
      ))}

      {/* Existing Summary.report {label, meta, url}; rendered only where the
          host opts in (pharmacy patient summary), so other tenants are unchanged. */}
      {showReportLink && reportUrl ? (
        <div className="dap-report-section">
          <div className="dap-report-label">
            <span>
              {summary.report.label}
              {summary.report.meta ? (
                <span className="meta"> {summary.report.meta}</span>
              ) : null}
            </span>
          </div>
          <div className="dap-report-text">
            <a href={reportUrl} target="_blank" rel="noreferrer">
              {reportUrl}
            </a>
          </div>
        </div>
      ) : null}

      {summary.clinical ? (
        <>
          <hr className="dap-report-divider" />
          <div className="dap-report-label">
            <span>
              {summary.clinical.label}
              {summary.clinical.meta ? (
                <span className="meta"> {summary.clinical.meta}</span>
              ) : null}
            </span>
          </div>
          <div className="dap-report-clinical">
            {(summary.clinical.rows || []).map((row) => (
              <div className="dap-report-row" key={row.label}>
                <span className="dap-report-metric">{row.label}</span>
                <span className="dap-report-right">
                  <span className="dap-report-val">{row.value}</span>
                  <b className={`dap-report-trend ${row.trend}`}>
                    {TREND_GLYPH[row.trend] || "→"}
                  </b>
                  <span className={`dap-report-pill ${row.tier}`}>
                    {copy.tiers[row.tier] || row.tier}
                  </span>
                </span>
              </div>
            ))}
          </div>
        </>
      ) : null}
    </div>
  );
};

export default SummaryReport;
